// In-memory working copy of the checklists, backed by ConfigStore.
//
// Read once when the inspector view mounts; the server is the durable copy, not
// the working copy. Writes happen on explicit save, never on keystroke.
//
// Every write returns the complete state from the server and replaces what is
// held here. Nothing is merged and nothing is optimistic: a save that fails
// leaves the panel showing what the server actually has, which is the thing the
// next call will use.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigStore, type LoadResult } from './ConfigStore';
import type { ChecklistConfig, ConfigBlob } from './types';

const EMPTY: ConfigBlob = { selectedConfigId: null, configs: [] };

export type ChecklistsApi = {
  configs: ChecklistConfig[];
  selectedConfigId: string | null;
  selectedConfig: ChecklistConfig | null;
  /** True until the first load settles. The call UI renders regardless. */
  loading: boolean;
  /** True while a write is in flight, so the settings UI can say so. */
  saving: boolean;
  /** Storage problem, surfaced in settings. Never blocks the call UI. */
  problem: string | null;
  dismissProblem: () => void;
  reload: () => void;
  selectConfig: (id: string) => Promise<void>;
  createConfig: (name: string) => Promise<ChecklistConfig | null>;
  saveConfig: (config: ChecklistConfig) => Promise<boolean>;
  deleteConfig: (id: string) => Promise<void>;
};

export function useChecklists(): ChecklistsApi {
  const [blob, setBlob] = useState<ConfigBlob>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // A response that lands after the component is gone, or after a later request
  // has already answered, must not overwrite what is on screen.
  const alive = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Applies a settled request. A failed one keeps the checklists already held
   * rather than blanking the panel mid-call - the endpoint being briefly
   * unreachable is not a reason to lose the field set on screen.
   */
  const settle = useCallback((result: LoadResult, mine: number): boolean => {
    if (!alive.current || mine !== generation.current) return false;
    setProblem(result.problem);
    if (!result.problem) setBlob(result.blob);
    return !result.problem;
  }, []);

  const begin = useCallback(() => {
    generation.current += 1;
    return generation.current;
  }, []);

  const load = useCallback(() => {
    const mine = begin();
    setLoading(true);
    void ConfigStore.load().then((result) => {
      settle(result, mine);
      if (alive.current && mine === generation.current) setLoading(false);
    });
  }, [begin, settle]);

  useEffect(load, [load]);

  /** Every write is the same shape: run it, take the state it returns. */
  const write = useCallback(
    async (run: () => Promise<LoadResult>): Promise<boolean> => {
      const mine = begin();
      setSaving(true);
      const result = await run();
      const ok = settle(result, mine);
      if (alive.current && mine === generation.current) setSaving(false);
      return ok;
    },
    [begin, settle],
  );

  const selectConfig = useCallback(
    async (id: string) => {
      await write(() => ConfigStore.select(id));
    },
    [write],
  );

  const createConfig = useCallback(
    async (name: string): Promise<ChecklistConfig | null> => {
      const mine = begin();
      setSaving(true);
      const result = await ConfigStore.create(name);
      settle(result, mine);
      if (alive.current && mine === generation.current) setSaving(false);
      return result.created;
    },
    [begin, settle],
  );

  const saveConfig = useCallback(
    (config: ChecklistConfig) => write(() => ConfigStore.save(config)),
    [write],
  );

  const deleteConfig = useCallback(
    async (id: string) => {
      await write(() => ConfigStore.remove(id));
    },
    [write],
  );

  const selectedConfig =
    blob.configs.find((config) => config.id === blob.selectedConfigId) ?? null;

  return {
    configs: blob.configs,
    selectedConfigId: blob.selectedConfigId,
    selectedConfig,
    loading,
    saving,
    problem,
    dismissProblem: () => setProblem(null),
    reload: load,
    selectConfig,
    createConfig,
    saveConfig,
    deleteConfig,
  };
}
